import React, { useState } from 'react'
import Button from 'components/shared/Button'
import { FormControlLabel, Checkbox, Tooltip, Chip } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import styles from 'components/forms/Checkbox/Checkbox.module.scss'
import strings from 'strings'

const DEMO_FIRM_SUGGESTIONS = ['medical', 'hospice', 'diagnostic', 'pharma']
const DEMO_INDIVIDUAL_SUGGESTIONS = ['smith', 'alvarez', 'chen']

const fieldWrap = { display: 'flex', flexDirection: 'column', flex: '1 1 200px', minWidth: 160 }
const fieldRow = { display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }
const inputStyle = { padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }
const labelStyle = { fontSize: 12, marginBottom: 4, color: '#555', display: 'flex', alignItems: 'center', gap: 4 }

const TOOLTIPS = {
    uei: 'Unique Entity Identifier — 12-character alphanumeric ID assigned by SAM.gov. Replaced DUNS in April 2022 as the primary federal ID for any entity doing business with the US government.',
    duns: 'Data Universal Numbering System — 9-digit ID from Dun & Bradstreet. Deprecated for federal use (superseded by UEI) but still widely referenced in older records.',
    cage: 'Commercial and Government Entity code — 5-character alphanumeric ID assigned by the Defense Logistics Agency that identifies suppliers to the US Department of Defense and other federal agencies.',
    npi: 'National Provider Identifier — 10-digit unique ID for US healthcare providers. Assigned by CMS via the NPPES registry.',
}

const SamgovSearchFields = ({ loading, handleSearch, isQuickSearch, isDemoMode, prefillTargets }) => {
    // In an application tab, prefill entity name from the business by default; a chip row lets
    // the user swap to search for an individual owner instead. useState-initializer form so
    // user edits aren't clobbered on re-render.
    const businessPrefill = prefillTargets?.business || ''
    const [entityName, setEntityName] = useState(() => businessPrefill)
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [selectedPrefill, setSelectedPrefill] = useState(() => businessPrefill ? 'business' : null)
    const [ueiSAM, setUeiSAM] = useState('')
    const [dunsNumber, setDunsNumber] = useState('')
    const [cageCode, setCageCode] = useState('')
    const [npi, setNpi] = useState('')
    const [activeOnly, setActiveOnly] = useState(true)

    const canSubmit = entityName || firstName || lastName || ueiSAM || dunsNumber || cageCode || npi

    const onSubmit = () => {
        if (loading) return
        handleSearch({
            entityName: entityName || null,
            firstName: firstName || null,
            lastName: lastName || null,
            ueiSAM: ueiSAM || null,
            dunsNumber: dunsNumber || null,
            cageCode: cageCode || null,
            npi: npi || null,
            activeOnly
        })
    }

    const labelWithTooltip = (text, tooltipText) => (
        <label style={labelStyle}>
            {text}
            {tooltipText && (
                <Tooltip title={tooltipText} arrow placement='top'>
                    <InfoOutlinedIcon style={{ fontSize: 14, color: '#888', cursor: 'help' }} />
                </Tooltip>
            )}
        </label>
    )

    const field = (labelEl, value, setter, id, placeholder = '') => (
        <div style={fieldWrap}>
            {labelEl}
            <input
                style={inputStyle}
                value={value}
                onChange={(e) => setter(e.target.value)}
                placeholder={placeholder}
                id={id}
            />
        </div>
    )

    const applyFirmSuggestion = (term) => {
        setEntityName(term)
        setFirstName('')
        setLastName('')
    }
    const applyIndividualSuggestion = (term) => {
        setLastName(term)
        setEntityName('')
        setFirstName('')
    }

    // Prefill picker — click a chip to swap search target between the business and each owner.
    // Runs the SAM.gov search in either Firm or Individual mode (SamGovUtils picks based on
    // whichever set of fields is populated), so we clear the "other side" on each pick.
    const selectBusiness = () => {
        setEntityName(businessPrefill)
        setFirstName('')
        setLastName('')
        setSelectedPrefill('business')
    }
    const selectOwner = (owner, index) => {
        setEntityName('')
        setFirstName(owner.firstName || '')
        setLastName(owner.lastName || '')
        setSelectedPrefill(`owner-${index}`)
    }
    const owners = prefillTargets?.owners || []
    const showPrefillPicker = businessPrefill || owners.length > 0

    const prefillChipStyle = (isSelected) => ({
        background: isSelected ? 'var(--rox-lilac-ink)' : 'var(--badge-review-bg)',
        color: isSelected ? '#fff' : 'var(--rox-lilac-ink)',
        fontWeight: isSelected ? 600 : 400,
    })

    return (
        <div>
            {showPrefillPicker && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-canvas)', borderRadius: 4, fontSize: 12 }}>
                    <div style={{ color: '#555', marginBottom: 6 }}>
                        <strong>Prefill from application</strong> — click to switch search target:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {businessPrefill && (
                            <Chip
                                label={`Business: ${businessPrefill}`}
                                size='small'
                                clickable
                                onClick={selectBusiness}
                                style={prefillChipStyle(selectedPrefill === 'business')}
                            />
                        )}
                        {owners.map((owner, i) => (
                            <Chip
                                key={i}
                                label={`Owner: ${owner.label}`}
                                size='small'
                                clickable
                                onClick={() => selectOwner(owner, i)}
                                style={prefillChipStyle(selectedPrefill === `owner-${i}`)}
                            />
                        ))}
                    </div>
                </div>
            )}
            {isDemoMode && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontSize: 12 }}>
                    <div style={{ color: '#555', marginBottom: 6 }}>
                        <strong>Demo mode suggestions</strong> — click to fill:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ color: '#666', minWidth: 90 }}>Firms →</span>
                        {DEMO_FIRM_SUGGESTIONS.map(term => (
                            <Chip
                                key={term}
                                label={term}
                                size='small'
                                clickable
                                onClick={() => applyFirmSuggestion(term)}
                                style={{ background: '#fff' }}
                            />
                        ))}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: '#666', minWidth: 90 }}>Individuals →</span>
                        {DEMO_INDIVIDUAL_SUGGESTIONS.map(term => (
                            <Chip
                                key={term}
                                label={term}
                                size='small'
                                clickable
                                onClick={() => applyIndividualSuggestion(term)}
                                style={{ background: '#fff' }}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div style={fieldRow}>
                {field(labelWithTooltip('Entity Name (Business)'), entityName, setEntityName, 'samgovEntityName', 'e.g. ACME MEDICAL LLC')}
                {field(labelWithTooltip('UEI (SAM)', TOOLTIPS.uei), ueiSAM, setUeiSAM, 'samgovUeiSAM')}
                {field(labelWithTooltip('DUNS', TOOLTIPS.duns), dunsNumber, setDunsNumber, 'samgovDuns')}
            </div>
            <div style={fieldRow}>
                {field(labelWithTooltip('First Name (Individual)'), firstName, setFirstName, 'samgovFirstName')}
                {field(labelWithTooltip('Last Name (Individual)'), lastName, setLastName, 'samgovLastName')}
                {field(labelWithTooltip('CAGE Code', TOOLTIPS.cage), cageCode, setCageCode, 'samgovCage')}
            </div>
            <div style={fieldRow}>
                {field(labelWithTooltip('NPI', TOOLTIPS.npi), npi, setNpi, 'samgovNpi')}
            </div>
            <div className='d-flex align-items-center justify-content-between mt-2' style={{ gap: '16px' }}>
                <FormControlLabel
                    className={styles.centeredCheckbox}
                    control={
                        <Checkbox
                            id='samgovActiveOnly'
                            checked={activeOnly}
                            onChange={(e) => setActiveOnly(e.target.checked)}
                            color='primary'
                        />
                    }
                    label='Active exclusions only'
                />
                <Button
                    onClick={onSubmit}
                    loading={loading}
                    disabled={!canSubmit}
                    id={`perform-samgov-search-${isQuickSearch ? 'qs' : 'app'}`}
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                    {strings.SEARCH}
                </Button>
            </div>
        </div>
    )
}

export default SamgovSearchFields
